/**
 * @file Buffs temporaires Money/Power/AtkSpeed (x3, x4 super) : 300 s par activation, cumul max 900 s, activation gratuite periodique ou payante (Dia).
 */
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { BuffId, BuffSpec } from '@config/types';
import type { SimContext } from '../Context';

export class BuffService {
  private readonly emitAcc = new Map<BuffId, number>();

  constructor(private readonly ctx: SimContext) {}

  spec(id: BuffId): BuffSpec {
    const s = this.ctx.config.balance.buffs.find((b) => b.id === id);
    if (!s) throw new Error(`Unknown buff ${id}`);
    return s;
  }

  /** Secondes avant la prochaine activation gratuite (0 = disponible). Utilise rt.wallMs. */
  freeReadyIn(id: BuffId): number {
    const s = this.spec(id);
    if (s.freeEverySeconds <= 0) return Number.POSITIVE_INFINITY;
    const elapsed = (this.ctx.rt.wallMs - this.ctx.data.buffs[id].lastFreeMs) / 1000;
    return Math.max(0, s.freeEverySeconds - elapsed);
  }

  /** Active un buff (+300 s, cumul <= 900 s) : cadeau gratuit periodique (useFree) ou achat en gemmes. */
  activate(id: BuffId, useFree: boolean): Result {
    const ctx = this.ctx;
    if (!ctx.services.unlocks.isFeatureUnlocked('buffs')) return fail('locked');
    const spec = this.spec(id);
    const st = ctx.data.buffs[id];
    if (st.seconds + spec.secondsPerActivation > spec.maxSeconds + 1e-6) return fail('maxLevel');
    if (useFree) {
      if (this.freeReadyIn(id) > 0) return fail('cooldown');
      st.lastFreeMs = ctx.rt.wallMs;
      this.addSeconds(id, spec.secondsPerActivation, false);
      return okVoid;
    }
    return ctx.tx.purchase(spec.cost, `buff:${id}`, () => {
      this.addSeconds(id, spec.secondsPerActivation, false);
      return okVoid;
    });
  }

  addSeconds(id: BuffId, seconds: number, superFlag: boolean): void {
    const spec = this.spec(id);
    const st = this.ctx.data.buffs[id];
    const wasActive = st.seconds > 0;
    st.seconds = Math.min(spec.maxSeconds, st.seconds + seconds);
    const becameSuper = superFlag && !st.superActive;
    if (superFlag) st.superActive = true;
    if (!wasActive || becameSuper) this.ctx.bus.emit('ModifiersDirty', { source: `buff:${id}` });
    this.ctx.bus.emit('BuffChanged', { id, seconds: st.seconds, superActive: st.superActive });
  }

  /** Decompte en temps de JEU (les buffs ne s'ecoulent pas hors-ligne). BuffChanged au plus 1x/s par buff. */
  tick(dt: number): void {
    const ctx = this.ctx;
    for (const spec of ctx.config.balance.buffs) {
      const st = ctx.data.buffs[spec.id];
      if (st.seconds <= 0) continue;
      st.seconds -= dt;
      if (st.seconds <= 0) {
        st.seconds = 0;
        st.superActive = false;
        this.emitAcc.delete(spec.id);
        ctx.bus.emit('ModifiersDirty', { source: `buff:${spec.id}` });
        ctx.bus.emit('BuffChanged', { id: spec.id, seconds: 0, superActive: false });
        continue;
      }
      const acc = (this.emitAcc.get(spec.id) ?? 0) + dt;
      if (acc >= 1) {
        this.emitAcc.set(spec.id, acc - 1);
        ctx.bus.emit('BuffChanged', { id: spec.id, seconds: st.seconds, superActive: st.superActive });
      } else {
        this.emitAcc.set(spec.id, acc);
      }
    }
  }
}
